import { useState, useEffect, useRef } from "react";
import { contactUs } from "../../../api/ContactUs";
import { validateEmail } from "../../shared/Utils/ValidateEmail";
import classes from "./ContactModal.module.css";
import { errorHandler } from "../../shared/Utils/ErrorHandler";
import { lineBreaks } from "../../shared/Utils/LineBreaks";
import formClasses from "../DashboardForm.module.css";
import SecondaryButton from "../../shared/Buttons/SecondaryButton";
import { Turnstile } from "@marsidev/react-turnstile";

export default function ContactModal({ closeModal }) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    reason: "",
  });
  const [feError, setFEError] = useState("");
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const turnstileRef = useRef(null);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const contactUsCall = async (formData) => {
    try {
      const response = await contactUs(formData);
      setSuccess("Your message has been sent successfully!");
      setError("");
    } catch (error) {
      let errorStr = errorHandler(error);
      const errorMessageWithBreaks = lineBreaks(errorStr);
      setError(errorMessageWithBreaks);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault(); // Prevent the default form submission behavior
    setError("");

    // Validation for the name length
    if (formData.name.length > 25) {
      setFEError("Name must be under 25 characters.");
      setSuccess("");
      return; // Stop further execution if validation fails
    }

    // Validation for the email format
    if (!validateEmail(formData.email)) {
      setFEError("Please enter a valid email address.");
      setSuccess("");
      return; // Stop further execution if validation fails
    }

    // Validation for Turnstile captcha
    if (!turnstileToken) {
      setFEError("Please complete the captcha verification.");
      setSuccess("");
      return;
    }

    setFEError("");

    // If all validations pass, make the API call with the turnstile token
    contactUsCall({ ...formData, turnstile_token: turnstileToken });

    // Reset Turnstile after successful submission
    setTimeout(() => {
      setTurnstileToken("");
      turnstileRef.current?.reset();
    }, 1000);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className={formClasses.modal_overlay}>
      <div className={`${formClasses.modal} !border !border-gray !rounded-3xl`}>
        <div
          className={`${classes.main_div} ${classes.contact_form_container}`}
        >
          <h1>Contact Us</h1>
          <button onClick={closeModal} className={classes.close_button}>
            x
          </button>
          <form className={classes.contact_us_form} onSubmit={handleSubmit}>
            <div className={classes.input_group}>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Name"
              />
              <input
                type="text"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Email"
              />
            </div>
            <input
              type="number"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="Phone Number"
            />
            <label className="block !w-full text-sm text-left">
              Reason for contacting us
            </label>
            <select
              name="reason"
              className="border border-gray-400 px-4 rounded h-12 text-base mb-4 w-full !text-gray-400"
              value={formData.reason}
              onChange={handleChange}
            >
              <option value="" disabled hidden>
                Select a reason
              </option>
              <option value="Affiliate" className="text-black">
                Affiliate
              </option>
              <option value="Donation" className="text-black">
                Donation
              </option>
              <option value="Technical Issue" className="text-black">
                Technical Issue
              </option>
              <option value="Consulting" className="text-black">
                Consulting
              </option>
              <option value="Foster Interest" className="text-black">
                Foster Interest
              </option>
              <option value="Resources" className="text-black">
                Resources
              </option>
              <option value="Other" className="text-black">
                Other
              </option>
            </select>
            <textarea
              type="text"
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Message"
            />
            <div className="mb-4">
              <Turnstile
                ref={turnstileRef}
                siteKey={process.env.REACT_APP_TURNSTILE_SITE_KEY}
                onSuccess={setTurnstileToken}
                onError={() => setTurnstileToken("")}
                onExpire={() => setTurnstileToken("")}
              />
            </div>
            <SecondaryButton
              type="submit"
              className="!w-48 !px-4 !py-2 !rounded-3xl "
            >
              Send
            </SecondaryButton>
            {feError && <div className="text-error">{feError}</div>}
            {error && <div className="text-error">{error}</div>}
            {success && <div className="text-success">{success}</div>}
          </form>
        </div>
      </div>
    </div>
  );
}
